// Phase H+1 Mini-Wave 5 · V12 User Layout · sidebar + topbar + mega-menu + ⌘K.
// V12 Achromatic Strict · italiano puro.
// Zero-touch: nessuna modifica a Dashboard legacy. Shell parallela montata su /v2/*.
import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import V12UserSidebar from "@/components/user_v12/V12UserSidebar";
import V12UserTopbar from "@/components/user_v12/V12UserTopbar";
import V12UserMegaMenu from "@/components/user_v12/V12UserMegaMenu";
import V12UserCommandPalette from "@/components/user_v12/V12UserCommandPalette";
import V12NotificationsDrawer from "@/components/admin_v12/V12NotificationsDrawer";
// Phase IX.F.6 · What's New palette (⌘⇧K · first-login tooltip).
import WhatsNewPalette from "@/components/v12/WhatsNewPalette";
// Phase VII.L.1 · Onboarding tour (first login OR ?demo=1).
import OnboardingTourMount from "@/components/onboarding/OnboardingTour";
// Wave VIII Frontend Integration · universal floating AI support chat
// (SupportEngine LIVE · Wave VIII.I). Mounted here so it appears on every
// authenticated user page without touching individual page components.
import AIChatWidget from "@/components/support/AIChatWidget";
import { useAuth } from "@/context/AuthContext";
import { isForbiddenRole, normaliseUserRole } from "@/data/v12_user_nav";

// MP.7 Hotfix R1 · Context marker to prevent double-chrome wrapping when a
// page uses SidebarLayout internally while a parent route already applies
// V12UserLayout (e.g. /app/notifications wraps externally AND Notifications.jsx
// wraps internally via SidebarLayout which, on /app/*, delegates to V12UserLayout).
const V12UserLayoutContext = createContext(false);
export const useInsideV12UserLayout = () => useContext(V12UserLayoutContext);

/**
 * V12UserLayout
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.pageTitle]
 * @param {number} [props.credits]
 * @param {Array} [props.notifications]
 */
export default function V12UserLayout({
    children, pageTitle = "Dashboard", notifications = [],
}) {
    const { user, loading } = useAuth();
    const alreadyInside = useInsideV12UserLayout();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [cmdOpen, setCmdOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);

    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdOpen((v) => !v);
            }
            if (e.key === "Escape") {
                setMegaOpen(false);
                setCmdOpen(false);
                setNotifOpen(false);
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // MP.7 Hotfix R1 · if a parent V12UserLayout is already mounted · skip the
    // chrome and just render children · prevents double sidebar/topbar.
    if (alreadyInside) {
        return <>{children}</>;
    }

    if (loading) {
        return (
            <div
                className="min-h-screen v12-bg flex items-center justify-center"
                data-v12-scope="user"
                data-testid="v12-user-layout-loading"
            >
                <span className="text-xs text-neutral-500 tracking-wide">
                    Caricamento…
                </span>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    // Role gating · ai_agent → redirect to /dashboard/legacy (escape hatch).
    // Phase I Wave 2.5 · we must point to the legacy explicit route instead
    // of `/dashboard` because the latter now redirects straight back to
    // `/app/dashboard`, which would cause an infinite loop for forbidden roles.
    if (isForbiddenRole(user.role)) {
        // eslint-disable-next-line no-console
        console.warn(
            `[V12UserLayout] Role '${user.role}' not allowed on /v2/dashboard. Redirecting to /dashboard/legacy.`,
        );
        return <Navigate to="/dashboard/legacy" replace />;
    }

    const normalisedRole = normaliseUserRole(user.role);

    return (
        <V12UserLayoutContext.Provider value={true}>
        <div
            className="min-h-screen v12-bg text-neutral-100 flex"
            data-v12-scope="user"
            data-testid="v12-user-layout"
            data-role={normalisedRole}
        >
            <V12UserSidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed((v) => !v)}
                role={normalisedRole}
            />
            <div className="flex-1 flex flex-col min-w-0">
                <V12UserTopbar
                    pageTitle={pageTitle}
                    onOpenMega={() => setMegaOpen(true)}
                    onOpenCmd={() => setCmdOpen(true)}
                    onOpenNotif={() => setNotifOpen(true)}
                    notificationsCount={notifications.length}
                />
                <main
                    className="flex-1 overflow-y-auto"
                    data-testid="v12-user-main"
                >
                    {children}
                </main>
            </div>

            <AnimatePresence>
                {megaOpen && (
                    <V12UserMegaMenu
                        onClose={() => setMegaOpen(false)}
                        role={normalisedRole}
                    />
                )}
                {cmdOpen && (
                    <V12UserCommandPalette
                        onClose={() => setCmdOpen(false)}
                        role={normalisedRole}
                    />
                )}
                {notifOpen && (
                    <V12NotificationsDrawer
                        onClose={() => setNotifOpen(false)}
                        items={notifications}
                    />
                )}
            </AnimatePresence>
            {/* Wave VIII Frontend Integration · universal AI chat widget.
                Only rendered if a parent V12UserLayout isn't already active
                (prevents double floating button when SidebarLayout re-wraps). */}
            {!alreadyInside && <AIChatWidget />}
            {/* Phase IX.F.6 · What's New palette (⌘⇧K · first-login tooltip). */}
            {!alreadyInside && <WhatsNewPalette />}
            {/* Phase VII.L.1 · Onboarding tour · first login + ?demo=1 trigger. */}
            {!alreadyInside && <OnboardingTourMount />}
        </div>
        </V12UserLayoutContext.Provider>
    );
}
